import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, makeStyles, Theme, Box } from '@material-ui/core';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import _ from 'lodash';
import { InvestorInvestmentCriteria, Industry, InvestorIndustryList } from '../../types/types';
import Button from '../../components/Button';
import RadioButtonsGroup from '../../components/RadioButtonGroup';
import GeographySelect from '../../components/GeographySelect';
import Form from '../../components/Form';
import IndustrySelect from '../../components/IndustrySelect';
import { StyledText } from '../../components/StyledText';
import { formikSubmitDisabled, dollarFormatInstruction, wholeDollarsOmitTrailingZeros } from '../../lib/utils';
import { investorPublishedInvestmentCriteriaSchema } from '../../lib/validationSchemas';
import DollarInput from '../../components/DollarInput';

const defaultMarketModelCriteria = {
  B2C: 'Sometimes',
  B2B: 'Sometimes',
  B2G: 'Sometimes',
  SaaS: 'Sometimes'
};

const initialValues = (profile: InvestorInvestmentCriteria) => ({
  investmentLow: profile.investmentLow ? wholeDollarsOmitTrailingZeros(profile.investmentLow) : profile.investmentLow,
  investmentHigh: profile.investmentHigh ? wholeDollarsOmitTrailingZeros(profile.investmentHigh) : profile.investmentHigh,
  investmentRevenue: profile.investmentRevenue ? wholeDollarsOmitTrailingZeros(profile.investmentRevenue) : profile.investmentRevenue,
  founderFemale: Boolean(profile.founderFemale) || false,
  founderUnderrepresented: Boolean(profile.founderUnderrepresented) || false,
  industryList: profile.industryList.map((i) => ({ ...i, label: i.industry })) || [],
  geographyList: profile.geographyList.map((i) => ({ ...i, label: i.state || i.country })) || [],
  marketModelCriteria: profile.marketModelCriteria || defaultMarketModelCriteria
});

const unpublishedValidationSchema = Yup.object().shape({
  investmentLow: Yup.number().nullable().min(0, 'Minimum $0'),
  investmentHigh: Yup.number().nullable()
    .when('investmentLow',
      { is: (investmentLow) => (investmentLow == null),
        then: Yup.number().nullable().min(0) })
    .when('investmentLow', {
      is: (investmentLow) => (investmentLow != null),
      then: Yup.number().nullable().min((Yup.ref('investmentLow')), 'Must be greater than or equal to Investment Low.')
    }),
  investmentRevenue: Yup.number().nullable(),
  founderFemale: Yup.boolean(),
  founderUnderrepresented: Yup.boolean(),
  industryList: Yup.array().of(Yup.object().shape({
    id: Yup.number().nullable(),
    industry: Yup.string(),
    label: Yup.string()
  })),
  geographyList: Yup.array().of(Yup.object().shape({
    id: Yup.number().nullable(),
    state: Yup.string().nullable(),
    country: Yup.string().nullable(),
    label: Yup.string()
  })),
  marketModelCriteria: Yup.object().shape({
    B2C: Yup.string().nullable().oneOf(['Never', 'Sometimes', 'Only', null]),
    B2B: Yup.string().nullable().oneOf(['Never', 'Sometimes', 'Only', null]),
    B2G: Yup.string().nullable().oneOf(['Never', 'Sometimes', 'Only', null]),
    SaaS: Yup.string().nullable().oneOf(['Never', 'Sometimes', 'Only', null])
  })
});

const getValidationSchema = (isPublished: boolean) => {
  if (isPublished) {
    return investorPublishedInvestmentCriteriaSchema;
  } else {
    return unpublishedValidationSchema;
  }
};

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    margin: theme.spacing(0),
    minWidth: 400
  },
  dialogActions: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '10px 24px'
  }
}));

interface InvestorCriteriaPanelEditProps {
  open: boolean
  isPublished: boolean
  investorCriteria: InvestorInvestmentCriteria
  handleSubmit: (values: InvestorInvestmentCriteria) => void
  handleClose: () => void
}

const InvestorCriteriaPanelEdit: React.FC<InvestorCriteriaPanelEditProps> = ({
  open, investorCriteria, isPublished, handleSubmit, handleClose
}) => {
  const styles = useStyles();
  const validationSchema = getValidationSchema(isPublished);
  const formik = useFormik({
    initialValues: initialValues(investorCriteria),
    validationSchema,
    enableReinitialize: true,
    onSubmit: handleSubmit,
    validateOnBlur: true,
    validateOnChange: true
  });
  const handleCancel = () => {
    handleClose();
    formik.resetForm();
  };

  const industryList = InvestorIndustryList;

  return (
    <Dialog
      open={open}
      className={styles.root}
      onClose={handleClose}
    >
      <Form formik={formik}>
        <DialogTitle disableTypography>
          <StyledText variant='h2'>Edit Investment Criteria</StyledText>
        </DialogTitle>
        <DialogContent>
          <DollarInput
            fullWidth
            id='investmentLow'
            label={`Investment Low ${dollarFormatInstruction}`}
            placeholder=''
          />
          <DollarInput
            fullWidth
            id='investmentHigh'
            label={`Investment High ${dollarFormatInstruction}`}
            placeholder=''
            minValue={formik.values.investmentLow}
          />
          <DollarInput
            fullWidth
            id='investmentRevenue'
            label={`Revenue Requirement ${dollarFormatInstruction}`}
            placeholder=''
            allowNegative
          />
          <GeographySelect
            id='geographyList'
            labelId='geographyList'
            label='Location'
            multiple
            showAnyCase
            value={formik.values.geographyList}
            error={formik.errors.geographyList ? 'You must select a location.' : undefined}
            touched={formik.touched.geographyList}
            onBlur={(arg) => {
              formik.handleBlur(arg);
              formik.setFieldTouched('geographyList', true, true);
            }}
          />
          <IndustrySelect
            id='industryList'
            label='Industries'
            multiple
            onBlur={formik.handleBlur}
            touched
            value={formik.values.industryList}
            error={formik.errors.industryList}
            options={industryList.map((i: Industry) => ({ value: i, label: i.industry }))}
          />
          <Box padding='24px' margin='20px 0' style={{ backgroundColor: '#F4F6F8', borderRadius: 5 }}>
            <StyledText variant='h3'>Market Model</StyledText>
            <RadioButtonsGroup
              formLabel='I invest in B2B'
              id='marketModelCriteria.B2B'
              options={[
                { label: 'Only', value: 'Only' },
                { label: 'Sometimes', value: 'Sometimes' },
                { label: 'Never', value: 'Never' }
              ]}
              value={formik.values.marketModelCriteria.B2B}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) => {
                formik.setFieldValue(event.target.name, value);
              }}
            />
            <RadioButtonsGroup
              formLabel='I invest in B2C'
              id='marketModelCriteria.B2C'
              options={[
                { label: 'Only', value: 'Only' },
                { label: 'Sometimes', value: 'Sometimes' },
                { label: 'Never', value: 'Never' }
              ]}
              value={formik.values.marketModelCriteria.B2C}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) => {
                formik.setFieldValue(event.target.name, value);
              }}
            />
            <RadioButtonsGroup
              formLabel='I invest in B2G'
              id='marketModelCriteria.B2G'
              options={[
                { label: 'Only', value: 'Only' },
                { label: 'Sometimes', value: 'Sometimes' },
                { label: 'Never', value: 'Never' }
              ]}
              value={formik.values.marketModelCriteria.B2G}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) => {
                formik.setFieldValue(event.target.name, value);
              }}
            />
            <RadioButtonsGroup
              formLabel='I invest in SaaS'
              id='marketModelCriteria.SaaS'
              options={[
                { label: 'Only', value: 'Only' },
                { label: 'Sometimes', value: 'Sometimes' },
                { label: 'Never', value: 'Never' }
              ]}
              value={formik.values.marketModelCriteria.SaaS}
              handleChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) => {
                formik.setFieldValue(event.target.name, value);
              }}
            />
          </Box>
          <RadioButtonsGroup
            formLabel='Requires Female Founder'
            id='founderFemale'
            options={[
              { label: 'No', value: false },
              { label: 'Yes', value: true }
            ]}
            value={formik.values.founderFemale}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) => {
              formik.setFieldValue(event.target.name, value === 'true');
            }}
          />
          <RadioButtonsGroup
            formLabel='Requires Underrepresented Management Team'
            id='founderUnderrepresented'
            options={[
              { label: 'No', value: false },
              { label: 'Yes', value: true }
            ]}
            value={formik.values.founderUnderrepresented}
            handleChange={(event: React.ChangeEvent<HTMLInputElement>, value: string) => {
              formik.setFieldValue(event.target.name, value === 'true');
            }}
          />
        </DialogContent>
        <DialogActions className={styles.dialogActions}>
          <Button type='button' variant='text' color='primary' onClick={handleCancel}>Cancel</Button>
          <Button type='submit' disabled={formikSubmitDisabled(formik)}>Save</Button>
        </DialogActions>
      </Form>
    </Dialog>
  );
};

export default InvestorCriteriaPanelEdit;

